import { Icon } from '../../components/Icons.jsx';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORMS, PLATFORM_LABELS } from './shared/platformLabels.js';

export function OverviewTab({ brandForm, publishingForm, platformPrefs = [], socialAccounts = [], strategy, onJumpTo, onAskAI, onConnectPlatform }) {
  const readiness = buildReadiness({ brandForm, publishingForm, platformPrefs, strategy });
  const summary = buildPlanSummary({ brandForm, publishingForm, platformPrefs, strategy });
  const channels = connectedChannelSummary(socialAccounts, platformPrefs);
  const nextAction = buildNextAction(readiness.items, strategy);

  return (
    <div className="cp-overview-grid">
      <section className="cp-command-card cp-command-card-primary">
        <div className="cp-command-topline">
          <span className="cp-status-pill cp-status-pill-good"><span /> Autopilot context</span>
          <span className="cp-score-ring" style={{ '--score': `${readiness.score}%` }}>{readiness.score}%</span>
        </div>
        <div className="cp-next-action">
          <span>{nextAction.label}</span>
          <h2>{nextAction.title}</h2>
          <p>{summary}</p>
        </div>
        <div className="cp-command-actions">
          <button className="btn btn-primary" onClick={() => onJumpTo?.(nextAction.tab)}>
            <Icon name={nextAction.icon} size={13} /> {nextAction.cta}
          </button>
          <button className="btn btn-secondary" onClick={() => onAskAI?.({ scope: 'content-plan.overview' })}>
            <Icon name="sparkles" size={13} /> Ask AI to review
          </button>
        </div>
      </section>

      <section className="cp-system-map" aria-label="Content Plan system map">
        {readiness.items.map((item) => (
          <button key={item.id} className="cp-system-node" onClick={() => onJumpTo?.(item.tab)}>
            <span className={'cp-node-dot ' + (item.ready ? 'is-ready' : '')} />
            <span className="cp-node-copy">
              <strong>{item.label}</strong>
              <span>{item.description}</span>
            </span>
            <span className="cp-node-status">{item.ready ? 'Ready' : item.status || 'Needs input'}</span>
          </button>
        ))}
      </section>

      <section className="cp-command-card">
        <div className="cp-panel-title-row">
          <div>
            <div className="settings-section-label">Month plan</div>
            <h3>Month at a glance</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onJumpTo?.('month')}>
            Manage <Icon name="arrow_r" size={12} />
          </button>
        </div>
        <div className="cp-mini-month">
          {['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((label, i) => (
            <div key={label} className={'cp-mini-week ' + (i === 0 && strategy ? 'is-ready' : '')}>
              <span>{label}</span>
              <strong>{i === 0 && strategy ? 'Ready to build' : 'Auto slot'}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="cp-command-card">
        <div className="cp-panel-title-row">
          <div>
            <div className="settings-section-label">Connected channels</div>
            <h3>{channels.connected.length ? 'Ready for generation' : 'Connect channels when ready'}</h3>
          </div>
          <span className="chip chip-active">{channels.connected.length} connected</span>
        </div>
        <div className="cp-platform-mini-row">
          {channels.connected.map((row) => (
            <span key={row.platform} className="cp-platform-mini" title={row.name}>
              <PlatformIcon platform={row.platform} size={16} />
              {PLATFORM_LABELS[row.platform] || row.platform}
            </span>
          ))}
          {channels.available.slice(0, Math.max(1, 6 - channels.connected.length)).map((platform) => (
            <button key={platform} className="cp-platform-mini cp-platform-mini-add" onClick={() => onConnectPlatform?.(platform)} type="button">
              <Icon name="plus" size={12} />
              Add {PLATFORM_LABELS[platform] || platform}
            </button>
          ))}
          {!channels.connected.length && !channels.available.length && <span className="caption muted">AI can generate drafts now. Connect channels when you want scheduling.</span>}
        </div>
      </section>
    </div>
  );
}

function connectedChannelSummary(socialAccounts, platformPrefs) {
  const connected = [];
  const seen = new Set();
  (socialAccounts || []).forEach((account) => {
    const platform = normalizePlatform(account.platform);
    if (!platform || seen.has(platform) || account.status !== 'connected') return;
    seen.add(platform);
    connected.push({ platform, name: account.displayName || PLATFORM_LABELS[platform] || platform });
  });
  const preferred = (platformPrefs || []).filter(row => row.enabled).map(row => normalizePlatform(row.platform)).filter(Boolean);
  const pool = preferred.length ? preferred : PLATFORMS;
  return {
    connected,
    available: pool.filter(platform => !seen.has(platform)).slice(0, 6),
  };
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function buildNextAction(items, strategy) {
  const firstMissing = items.find((item) => !item.ready && !item.autoReady);
  if (firstMissing) {
    return {
      label: 'Next best action',
      title: `${firstMissing.label} needs a quick check.`,
      cta: `Open ${firstMissing.label}`,
      tab: firstMissing.tab,
      icon: firstMissing.icon || 'arrow_r',
    };
  }
  return strategy
    ? { label: 'Ready', title: 'The system has enough context to work.', cta: 'Build month', tab: 'month', icon: 'calendar' }
    : { label: 'Ready', title: 'Brand inputs are ready. Generate the plan next.', cta: 'Generate plan', tab: 'plan', icon: 'target' };
}

function buildReadiness({ brandForm, publishingForm, platformPrefs, strategy }) {
  const items = [
    {
      id: 'brand',
      tab: 'brand',
      label: 'Brand inputs',
      description: 'Audience, offer, voice, guardrails',
      icon: 'sparkles',
      ready: !!brandForm?.businessDescription?.trim() && !!brandForm?.targetAudience?.trim(),
    },
    {
      id: 'plan',
      tab: 'plan',
      label: 'Content Plan',
      description: 'Positioning, pillars, metrics',
      icon: 'target',
      ready: !!strategy,
    },
    {
      id: 'month',
      tab: 'month',
      label: 'Month plan',
      description: `${Number(publishingForm?.postsPerWeek) || 0} posts/week baseline`,
      icon: 'calendar',
      ready: !!strategy && Number(publishingForm?.postsPerWeek) > 0,
    },
    {
      id: 'platforms',
      tab: 'plan',
      label: 'Channel style',
      description: 'Each network gets its own post style',
      icon: 'layers',
      ready: platformPrefs.some((p) => p.enabled),
      autoReady: true,
      status: 'Auto',
    },
  ];
  const score = Math.round((items.filter((x) => x.ready || x.autoReady).length / items.length) * 100);
  return { score, items };
}

function buildPlanSummary({ brandForm, publishingForm, platformPrefs, strategy }) {
  const inner = strategy?.strategy ?? {};
  if (inner.summary || strategy?.summary) return inner.summary || strategy.summary;
  const name = brandForm?.businessName || 'Your brand';
  const cadence = publishingForm?.postsPerWeek ? `${publishingForm.postsPerWeek} posts/week` : 'a steady cadence';
  const channels = platformPrefs.filter((p) => p.enabled).map((p) => PLATFORM_LABELS[p.platform] || p.platform);
  const channelText = channels.length ? channels.slice(0, 3).join(', ') : 'your connected channels';
  return `${name} will run ${cadence} across ${channelText}, with AI adapting hooks, format, and tone per platform.`;
}
